import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import {
  onAuthStateChanged,
  signInWithPopup,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signOut,
  type User as FirebaseUser,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db, isDevMode } from '@/lib/firebase';
import { setAuthProvider } from '@/api/client';
import { resolveTenantAccess } from './tenant-role';

interface User {
  uid: string;
  email: string | null;
  displayName: string | null;
  companyId?: string;
  role?: string;         // from Firestore (legacy — used by admin portal)
  tokenRole?: string;    // from Firebase custom claims (used by tenant selector)
  tenantRole?: string;   // resolved role within the tenant (admin/manager/support/viewer)
}

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  loginWithGoogle: () => Promise<void>;
  loginWithEmail: (email: string, password: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/* ── Dev mode bypass ─────────────────────────────── */

const DEV_USER: User = {
  uid: 'dev-user-001',
  email: 'dev@chargeback.local',
  displayName: 'Dev User',
  companyId: 'peoplefinders',
  role: 'admin',
  tokenRole: (import.meta.env.VITE_DEV_ROLE as string | undefined) ?? 'tenant_admin',
  tenantRole: (import.meta.env.VITE_DEV_TENANT_ROLE as string) ?? 'admin',
};

function DevAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(DEV_USER);

  useEffect(() => {
    setAuthProvider(async () => 'dev-token');
  }, []);

  const loginWithGoogle = async () => { setUser(DEV_USER); };
  const loginWithEmail = async (_email: string, _password: string) => { setUser(DEV_USER); };
  const resetPassword = async (_email: string) => { /* no-op in dev */ };
  const logout = async () => { setUser(null); };

  return (
    <AuthContext.Provider value={{ user, loading: false, loginWithGoogle, loginWithEmail, resetPassword, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

/* ── Production Firebase auth ────────────────────── */

const googleProvider = new GoogleAuthProvider();

/**
 * Look up tenant identity and role: users/{uid} + user_mappings/{email} + custom claims.
 *
 * BOTH documents are read, always. `users/{uid}` used to return early, but
 * nothing writes `tenantRole` onto a `users/{uid}` document — so that
 * short-circuit meant a member whose custom claims failed to set could never
 * see the role that was recorded for them, and graded `viewer` forever. The
 * backend was changed for exactly this reason; see `resolveTenantAccess`.
 *
 * The user is permitted to read their own mapping — `firestore.rules`:
 *   match /user_mappings/{email} {
 *     allow read: if request.auth != null && request.auth.token.email == email;
 *   }
 */
async function resolveUserProfile(fbUser: FirebaseUser): Promise<User> {
  const mapped: User = {
    uid: fbUser.uid,
    email: fbUser.email,
    displayName: fbUser.displayName,
  };

  let claims: Record<string, unknown> = {};
  try {
    const claimsResult = await fbUser.getIdTokenResult();
    claims = claimsResult.claims as Record<string, unknown>;
  } catch {
    // non-fatal — resolution continues from Firestore
  }

  let userDoc: Record<string, unknown> | undefined;
  try {
    const snapshot = await getDoc(doc(db, 'users', fbUser.uid));
    if (snapshot.exists()) userDoc = snapshot.data();
  } catch {
    // Firestore lookup failed — continue to the mapping and the claims
  }

  let mapping: Record<string, unknown> | undefined;
  if (fbUser.email) {
    try {
      const snapshot = await getDoc(doc(db, 'user_mappings', fbUser.email));
      if (snapshot.exists()) mapping = snapshot.data();
    } catch {
      // Firestore lookup failed — continue to the claims
    }
  }

  const access = resolveTenantAccess({ uid: fbUser.uid, claims, userDoc, mapping });
  if (!access) {
    // No tenant identity: refused (explicit non-active status) or unresolvable.
    // Deliberately leaves companyId, role, tokenRole and tenantRole all unset —
    // the backend returns 403 for this user, and the UI must not grant more.
    return mapped;
  }

  mapped.companyId = access.companyId;
  mapped.role = access.role;
  mapped.tokenRole = access.tokenRole;
  mapped.tenantRole = access.tenantRole;

  return mapped;
}

function FirebaseAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setAuthProvider(async () => {
      const currentUser = auth.currentUser;
      if (!currentUser) return null;
      return currentUser.getIdToken();
    });

    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      if (fbUser) {
        const resolved = await resolveUserProfile(fbUser);
        setUser(resolved);
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const loginWithGoogle = async () => {
    await signInWithPopup(auth, googleProvider);
  };

  const loginWithEmail = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  const resetPassword = async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  };

  const logout = async () => {
    await signOut(auth);
  };

  return (
    <AuthContext.Provider value={{ user, loading, loginWithGoogle, loginWithEmail, resetPassword, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

/* ── Exported provider (auto-selects dev vs prod) ── */

export function AuthProvider({ children }: { children: ReactNode }) {
  if (isDevMode) {
    return <DevAuthProvider>{children}</DevAuthProvider>;
  }
  return <FirebaseAuthProvider>{children}</FirebaseAuthProvider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
